import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Reveal } from "../components/Reveal";
import { buildWhatsAppLink } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowUpRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) => {
  if (price === null || price === undefined) return null;
  return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 0 }).format(price);
};

// Chrono24 blocks hotlinking — proxy through images.weserv.nl (free, no auth)
const proxyImage = (url) => {
  if (!url) return url;
  if (url.includes("img.chrono24.com")) {
    return `https://images.weserv.nl/?url=${encodeURIComponent(url.replace(/^https?:\/\//, ""))}&w=800&h=800&fit=cover`;
  }
  return url;
};

const Catalog = () => {
  const { t, lang } = useLanguage();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBrand, setActiveBrand] = useState("all");
  const [error, setError] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/watches`)
      .then((r) => {
        if (!cancelled) setWatches(r.data || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const brands = useMemo(() => {
    const s = new Set(watches.map((w) => w.brand));
    return ["all", ...Array.from(s).sort()];
  }, [watches]);

  const filtered = useMemo(() => {
    if (activeBrand === "all") return watches;
    return watches.filter((w) => w.brand === activeBrand);
  }, [watches, activeBrand]);

  const allLabel = lang === "de" ? "Alle" : "All";
  const onRequestLabel = lang === "de" ? "Preis auf Anfrage" : "Price on request";
  const inquireLabel = lang === "de" ? "Anfrage via WhatsApp" : "Inquire via WhatsApp";
  const viewListing = lang === "de" ? "Inserat ansehen" : "View listing";
  const emptyLabel = lang === "de" ? "Keine Uhren gefunden." : "No watches found.";
  const loadingLabel = lang === "de" ? "Katalog wird geladen…" : "Loading catalog…";

  const buildMsg = (w) => {
    const intro =
      lang === "de"
        ? "Guten Tag, ich interessiere mich für die folgende Uhr aus Ihrem Katalog:"
        : "Good day, I am interested in the following watch from your catalog:";
    const priceLine =
      w.price_chf !== null && w.price_chf !== undefined
        ? `${lang === "de" ? "Preis" : "Price"}: CHF ${formatPrice(w.price_chf)}`
        : onRequestLabel;
    return `${intro}\n\n${w.brand} ${w.model}${w.reference ? ` · Ref. ${w.reference}` : ""}\n${priceLine}\n${w.chrono24_url || ""}`;
  };

  return (
    <div data-testid="catalog-page" className="bg-white">
      {/* Header */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-20 px-6 md:px-12">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — {t.catalog.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-aw text-5xl md:text-6xl lg:text-7xl font-light tracking-tight max-w-4xl leading-[1.05]">
              {t.catalog.heading}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-sans-aw text-base md:text-lg text-black/70 leading-relaxed mt-8 max-w-2xl">
              {t.catalog.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Brand filter */}
      <section className="px-6 md:px-12 pb-10">
        <div className="max-w-[1500px] mx-auto border-t border-black/10 pt-8">
          <div className="flex flex-wrap gap-x-2 gap-y-3" data-testid="brand-filter">
            {brands.map((b) => (
              <button
                key={b}
                data-testid={`filter-${b.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                onClick={() => setActiveBrand(b)}
                className={`font-sans-aw text-[11px] tracking-luxury uppercase px-4 py-2 border transition-colors duration-300 ${
                  activeBrand === b
                    ? "bg-black text-white border-black"
                    : "bg-white text-black/60 border-black/15 hover:text-black hover:border-black"
                }`}
              >
                {b === "all" ? allLabel : b}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section data-testid="catalog-grid-section" className="px-6 md:px-12 pb-32 md:pb-40">
        <div className="max-w-[1500px] mx-auto">
          {loading && (
            <p className="font-sans-aw text-sm text-black/50 py-20 text-center">{loadingLabel}</p>
          )}
          {error && !loading && (
            <p className="font-sans-aw text-sm text-black/60 py-20 text-center">{error}</p>
          )}
          {!loading && !error && filtered.length === 0 && (
            <p className="font-sans-aw text-sm text-black/50 py-20 text-center">{emptyLabel}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((w, idx) => {
              const priceStr =
                w.price_chf !== null && w.price_chf !== undefined
                  ? `CHF ${formatPrice(w.price_chf)}`
                  : onRequestLabel;
              return (
                <Reveal key={w.id} delay={(idx % 3) * 0.08}>
                  <article
                    data-testid={`watch-card-${w.id}`}
                    className="group flex flex-col"
                  >
                    <div className="relative overflow-hidden bg-[#f5f5f5] h-[360px] md:h-[420px]">
                      {(((w.images && w.images[0]) || w.image_url) && !brokenImages[w.id]) ? (
                        <img
                          src={proxyImage((w.images && w.images[0]) || w.image_url)}
                          alt={`${w.brand} ${w.model}`}
                          loading="lazy"
                          onError={() => setBrokenImages((s) => ({ ...s, [w.id]: true }))}
                          className="w-full h-full object-cover gallery-img"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-[#f5f5f5]" data-testid={`watch-placeholder-${w.id}`}>
                          <img src="https://customer-assets.emergentagent.com/job_38fd9250-debc-4672-ac8b-aeac6e1e6534/artifacts/253xhyku_ChatGPT%20Image%2014.%20Juni%202026%2C%2017_40_14.png" alt="Alpha Watch" className="w-20 h-20 opacity-15" />
                          <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/30 mt-6">Bild auf Anfrage</p>
                        </div>
                      )}
                    </div>
                    <div className="pt-6">
                      <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50">
                        {w.brand}
                      </p>
                      <h3 className="font-serif-aw text-xl md:text-2xl font-normal mt-2 leading-snug">
                        {w.model}
                      </h3>
                      {w.reference && (
                        <p className="font-sans-aw text-xs tracking-luxury-md uppercase text-black/45 mt-1">
                          Ref. {w.reference}
                        </p>
                      )}
                      {w.description && (
                        <p className="font-sans-aw text-sm text-black/65 mt-3 leading-relaxed">
                          {w.description}
                        </p>
                      )}
                      <div className="mt-5 flex items-baseline justify-between border-t border-black/10 pt-4">
                        <span className="font-serif-aw text-lg md:text-xl">{priceStr}</span>
                      </div>
                      <div className="mt-5 flex flex-wrap gap-3">
                        <a
                          data-testid={`watch-whatsapp-${w.id}`}
                          href={buildWhatsAppLink(buildMsg(w))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 font-sans-aw text-[10px] tracking-luxury uppercase bg-black text-white px-4 py-3 hover:bg-white hover:text-black border border-black transition-colors duration-500"
                        >
                          <MessageCircle size={13} strokeWidth={1.5} />
                          {inquireLabel}
                        </a>
                        {w.chrono24_url && (
                          <a
                            data-testid={`watch-chrono24-${w.id}`}
                            href={w.chrono24_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 font-sans-aw text-[10px] tracking-luxury uppercase border border-black/15 hover:border-black px-4 py-3 transition-colors duration-300"
                          >
                            {viewListing}
                            <ArrowUpRight size={13} strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Catalog;
